import type { CostsReport } from '../../types/reports';
import type { Segment } from '../reportsView';
import { formatMoney } from '../ownerOverview';
import { APPROXIMATE_NOTE, intLabel, moneyLabel, percentLabel, plural, strong, text, UNKNOWN, unitCostLabel, variationText, type CoverageBarView, type RowView, type SectionView, type StatView } from './shared';

/**
 * P-084 · R5 «¿Cómo van mis costos?» — presentación pura. La frase se compone con la cobertura, lo aproximado y los
 * bloqueos del sobre (K3), nunca con el nombre del estado. La barra de cobertura tiene DOS partes (con costo conocido /
 * sin costo) y la mezcla por fuente se lista como diagnóstico (K5). Loyverse no tiene historia de costo (se dice).
 */
export interface CostsView {
  say: Segment[];
  stats: StatView[];
  coverage: CoverageBarView | null;
  mix: Array<{ key: string; label: string; value: string }>;
  comparison: string | null;
  cta: { label: string; destination: string } | null;
  detail: SectionView[];
  notes: string[];
  empty: { title: string; description: string } | null;
}

export const COSTS_EMPTY_TITLE = 'No hubo ventas en este periodo.';
export const COSTS_EMPTY_TEXT = 'Sin ventas no hay costo de lo vendido que mostrar.';
export const COSTS_NOT_PROFIT_NOTE = 'Esto no es tu ganancia: faltan renta, sueldos y servicios. Eso lo ve «¿Cómo va mi negocio?».';
export const COSTS_PARTIAL_NOTE = 'El costo de la parte que no conozco no está restado: el margen conocido habla solo de lo que sí tiene costo.';

export function costsReportView(report: CostsReport): CostsView {
  const { current, envelope, comparison } = report;
  const notes: string[] = [];
  if (envelope.hasApproximateCost) notes.push(APPROXIMATE_NOTE);
  if (current.coveragePercent < 100 && current.coveragePercent > 0) notes.push(COSTS_PARTIAL_NOTE);
  notes.push(report.products.note, COSTS_NOT_PROFIT_NOTE);

  if (current.netSales !== null && Number(current.netSales) === 0) {
    return { say: [text(COSTS_EMPTY_TITLE)], stats: statsOf(report), coverage: null, mix: [], comparison: null, cta: null, detail: [], notes, empty: { title: COSTS_EMPTY_TITLE, description: COSTS_EMPTY_TEXT } };
  }

  // La frase (K3): cobertura, aproximado y bloqueos, en ese orden de verdad.
  const say: Segment[] = [];
  const approx = envelope.hasApproximateCost ? ' aprox.' : '';
  const coverageText = `${Math.round(current.coveragePercent)} %`;
  const nonCoverageBlockers = envelope.blockers.filter((blocker) => blocker.code !== 'COST_COVERAGE');
  if (current.costOfGoodsSold !== null && current.knownMargin !== null) {
    if (current.coveragePercent >= 100) {
      say.push(text('Lo que vendiste te costó '), strong(`${formatMoney(current.costOfGoodsSold)}${approx}`, true), text(': te quedaron '), strong(`${formatMoney(current.knownMargin)}${approx}`, true), text(' antes de gastos.'));
    } else {
      say.push(text(`De las ventas cuyo costo conocemos (${coverageText}), te costaron `), strong(`${formatMoney(current.costOfGoodsSold)}${approx}`, true), text(' y dejaron '), strong(`${formatMoney(current.knownMargin)}${approx}`, true), text(' antes de gastos. Del resto no sé el costo.'));
    }
  } else if (current.netSales === null) {
    say.push(text('Hay ventas en más de una moneda: no puedo sumar su costo.'));
  } else if (nonCoverageBlockers.length > 0) {
    say.push(text(nonCoverageBlockers[0]!.humanText));
    if (current.coveragePercent > 0) say.push(text(` Mientras tanto, tengo costo para el ${coverageText} de tus ventas.`));
  } else if (current.coveragePercent <= 0) {
    say.push(text('Todavía no sé el costo de nada de lo que vendes.'));
  } else {
    say.push(text(`Todavía no sé bien lo que te cuesta vender: tengo costo para el ${coverageText} de tus ventas, y hace falta más para publicar una cifra.`));
  }

  const coverage: CoverageBarView | null = current.costedNetSales !== null && current.missingCostNetSales !== null
    ? { knownPercent: Math.min(100, Math.max(0, Math.round(current.coveragePercent))), knownLabel: `Con costo conocido ${formatMoney(current.costedNetSales)}`, missingLabel: `Sin costo ${formatMoney(current.missingCostNetSales)}` }
    : null;
  const mix = [
    { key: 'recipe', label: 'Con receta (punto de venta)', value: moneyLabel(report.mix.recipeNetSales) },
    { key: 'loyverse', label: 'Con receta (Loyverse)', value: moneyLabel(report.mix.loyverseCostedNetSales) },
    { key: 'approximate', label: 'Con costo aproximado', value: moneyLabel(report.mix.approximateNetSales) },
    { key: 'missing', label: 'Sin costo', value: moneyLabel(report.mix.missingNetSales) },
  ].filter((row) => row.value !== UNKNOWN && Number(row.value.replace(/[^0-9.-]/g, '')) !== 0 || row.key === 'missing');

  let comparisonText: string | null = null;
  if (comparison.comparable && comparison.costVariation) {
    const cost = variationText(comparison.costVariation, 'percent');
    comparisonText = `Contra el periodo anterior, el costo de lo vendido fue ${cost.text}${comparison.knownMarginVariation ? ` y el margen conocido ${variationText(comparison.knownMarginVariation, 'money').text}` : ''}.`;
  } else if (comparison.reason) {
    comparisonText = comparison.reason;
  }

  const coverageBlocker = envelope.blockers.find((blocker) => blocker.code === 'COST_COVERAGE');
  const cta = coverageBlocker?.ctaLabel && coverageBlocker.ctaDestination ? { label: coverageBlocker.ctaLabel, destination: coverageBlocker.ctaDestination } : null;

  const productRows: RowView[] = report.products.rows.map((row) => ({
    key: row.key,
    title: row.name,
    subtitle: row.direction === 'new' ? 'Sin costo en el periodo anterior.' : row.previousUnitCost === null ? null : `Antes ${unitCostLabel(row.previousUnitCost)} por unidad.`,
    meta: [`${intLabel(row.units)} ${plural(row.units, 'unidad', 'unidades')}`, row.mode === 'APPROXIMATE' ? 'costo aproximado' : row.mode === 'MIXED' ? 'receta y aproximado' : 'receta'],
    value: `${unitCostLabel(row.unitCost)}${row.mode !== 'RECIPE' ? ' aprox.' : ''}`,
    valueTone: 'default',
    badge: row.change && row.direction !== 'flat' && row.direction !== 'new' ? { text: `${variationText(row.change, 'percent').text}`, tone: row.direction === 'up' ? 'warning' : 'success' } : row.direction === 'new' ? { text: 'Nuevo', tone: 'neutral' } : null,
    figures: [],
    destination: null,
  }));
  const ingredientRows: RowView[] = report.ingredients.rows.map((row) => ({
    key: row.ingredientId,
    title: row.name,
    subtitle: `Antes ${unitCostLabel(row.previousUnitCost)} por ${row.unitSymbol || 'unidad'}.`,
    meta: [],
    value: `${unitCostLabel(row.currentUnitCost)} / ${row.unitSymbol || 'unidad'}`,
    valueTone: row.direction === 'up' ? 'danger' : row.direction === 'down' ? 'success' : 'default',
    badge: { text: row.direction === 'flat' ? 'Sin cambio material' : `${row.changePercent > 0 ? '+' : ''}${Math.round(row.changePercent)} %`, tone: row.direction === 'up' ? 'warning' : row.direction === 'down' ? 'success' : 'neutral' },
    figures: [],
    destination: null,
  }));
  const ingredientsEmpty = !report.meta.history.canCompare
    ? 'Todavía no tengo compras suficientes para comparar precios.'
    : report.ingredients.onlyCurrent + report.ingredients.onlyPrevious > 0
      ? 'Ningún insumo tiene compras en los dos periodos: no hay precios que comparar.'
      : 'No hubo compras descontadas al inventario en el periodo.';

  const detail: SectionView[] = [
    { key: 'products', title: 'Costo por producto (punto de venta)', rows: productRows, empty: 'Ningún producto del punto de venta tiene costo completo en el periodo.', note: report.products.rows.length > 0 ? 'Costo por unidad vendida, con los snapshots de cada venta: cambiar hoy una receta no reescribe el pasado.' : null, more: report.products.moreCount > 0 ? `${report.products.moreCount} ${plural(report.products.moreCount, 'producto más', 'productos más')}.` : null },
    { key: 'ingredients', title: 'Insumos que cambiaron de precio', rows: ingredientRows, empty: ingredientsEmpty, note: report.ingredients.rows.length > 0 ? `${report.ingredients.note} ${report.ingredients.onlyCurrent > 0 ? `${report.ingredients.onlyCurrent} ${plural(report.ingredients.onlyCurrent, 'insumo', 'insumos')} solo con compras en este periodo.` : ''}`.trim() : null, more: report.ingredients.moreCount > 0 ? `${report.ingredients.moreCount} ${plural(report.ingredients.moreCount, 'insumo más', 'insumos más')}.` : null },
  ];

  return { say, stats: statsOf(report), coverage, mix, comparison: comparisonText, cta, detail, notes, empty: null };
}

function statsOf(report: CostsReport): StatView[] {
  const { current, comparison, envelope } = report;
  const approx = envelope.hasApproximateCost ? ' aprox.' : '';
  return [
    { key: 'cogs', label: 'Costo de lo vendido', value: current.costOfGoodsSold === null ? UNKNOWN : `${formatMoney(current.costOfGoodsSold)}${approx}`, hint: current.costOfGoodsSold === null ? 'no se puede publicar todavía' : null, tone: current.costOfGoodsSold === null ? 'muted' : 'default' },
    { key: 'coverage', label: 'Cobertura de costo', value: current.coverageFraction ?? UNKNOWN, hint: `${Math.round(current.coveragePercent)} % de tus ventas`, tone: current.coveragePercent >= 90 ? 'default' : current.coveragePercent >= 60 ? 'warning' : 'danger' },
    { key: 'margin', label: 'Margen conocido', value: current.knownMargin === null ? UNKNOWN : `${formatMoney(current.knownMargin)}${approx}`, hint: current.knownMarginPercent === null ? 'sobre lo costeado' : `${percentLabel(current.knownMarginPercent)} de lo costeado`, tone: current.knownMargin === null ? 'muted' : 'default' },
    { key: 'previous', label: 'Vs. periodo anterior', value: comparison.comparable && comparison.costVariation ? variationText(comparison.costVariation, 'percent').text : report.previous === null ? UNKNOWN : 'no comparable', hint: comparison.comparable ? 'costo de lo vendido' : report.previous === null ? 'sin historia' : 'la cobertura cambió', tone: comparison.comparable ? 'default' : 'muted' },
  ];
}
